import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiExternalLink, FiCopy, FiTrash2, FiBarChart2, FiClock, FiLock, FiZap, FiRefreshCw, FiGrid, FiDownload } from 'react-icons/fi';
import { useStore } from '../../store';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import QRModal from '../QRModal/QRModal';

dayjs.extend(relativeTime);

const RecentLinks: React.FC = () => {
  const { recentUrls, fetchRecentUrls, deleteUrl, isLoading } = useStore();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedQR, setSelectedQR] = useState<any>(null);
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    // Load from local storage first (handled by Zustand persist)
    // Then fetch from server if user is logged in
    if (recentUrls.length === 0) {
      fetchRecentUrls();
    }
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchRecentUrls();
    setIsRefreshing(false);
    toast.success('Links refreshed');
  };

  const handleViewQR = (url: any) => {
    setSelectedQR(url);
    setShowQRModal(true);
  };

  const handleDownloadQR = (url: any) => {
    if (url.qrCode) {
      const link = document.createElement('a');
      link.href = url.qrCode;
      link.download = `qr-${url.hash}.png`;
      link.click();
      toast.success('QR code downloaded');
    } else {
      toast.error('QR code not available');
    }
  };

  const copyToClipboard = async (url: string) => {
    await navigator.clipboard.writeText(url);
    toast.success('Copied to clipboard!');
  };

  const handleDelete = async (hash: string) => {
    if (window.confirm('Are you sure you want to delete this link?')) {
      try {
        await deleteUrl(hash);
        toast.success('Link deleted successfully');
      } catch (error) {
        toast.error('Failed to delete link');
      }
    }
  };

  const truncateUrl = (url: string, maxLength: number = 50) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (recentUrls.length === 0) {
    return (
      <div className="card text-center py-12">
        <FiClock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No recent links</h3>
        <p className="text-gray-600">Your shortened URLs will appear here</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900">Recent Links</h2>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-600 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors"
        >
          <FiRefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>
      
      <div className="space-y-4">
        {recentUrls.map((url) => (
          <div
            key={url.hash}
            className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                {/* Short URL */}
                <div className="flex items-center space-x-2 mb-2">
                  <span className="font-mono text-sm text-primary-600 truncate">
                    {url.shortUrl}
                  </span>
                  <button
                    onClick={() => copyToClipboard(url.shortUrl)}
                    className="text-gray-500 hover:text-gray-700"
                    title="Copy to clipboard"
                  >
                    <FiCopy className="w-4 h-4" />
                  </button>
                  <a
                    href={url.shortUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-gray-700"
                    title="Open link"
                  >
                    <FiExternalLink className="w-4 h-4" />
                  </a>
                </div>

                {/* Original URL */}
                <p className="text-sm text-gray-600 truncate mb-2">
                  {truncateUrl(url.originalUrl)}
                </p>

                {/* Stats */}
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span className="flex items-center">
                    <FiBarChart2 className="w-3 h-3 mr-1" />
                    {url.clicks}{url.maxClicks ? `/${url.maxClicks}` : ''} clicks
                  </span>
                  <span className="flex items-center">
                    <FiClock className="w-3 h-3 mr-1" />
                    {dayjs(url.createdAt).fromNow()}
                  </span>
                  {url.isPasswordProtected && (
                    <span className="flex items-center text-primary-600">
                      <FiLock className="w-3 h-3 mr-1" />
                      Password Protected
                    </span>
                  )}
                  {url.burnAfterReading && (
                    <span className="flex items-center text-orange-600">
                      <FiZap className="w-3 h-3 mr-1" />
                      {url.clicks >= 1 ? 'Burned' : 'Burns after first click'}
                    </span>
                  )}
                  {url.expiresAt && !url.burnAfterReading && (
                    <span className="text-warning">
                      Expires {dayjs(url.expiresAt).fromNow()}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2 ml-4">
                {/* QR Code Actions */}
                {url.qrCode && (
                  <>
                    <button
                      onClick={() => handleViewQR(url)}
                      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
                      title="View QR Code"
                    >
                      <FiGrid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDownloadQR(url)}
                      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
                      title="Download QR Code"
                    >
                      <FiDownload className="w-4 h-4" />
                    </button>
                  </>
                )}
                <Link
                  to={`/analytics/${url.hash}`}
                  className="p-2 text-gray-500 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
                  title="View analytics"
                >
                  <FiBarChart2 className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => handleDelete(url.hash)}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                  title="Delete link"
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* QR Modal */}
      <QRModal
        url={selectedQR}
        isOpen={showQRModal}
        onClose={() => {
          setShowQRModal(false);
          setSelectedQR(null);
        }}
      />
    </div>
  );
};

export default RecentLinks;